<template>
  <div class="card">
    <div class="card-header">
      <div class="card-header-title">
        <div class="title is-6 is-marginless">
          Promo Cards <span class="tag">Προσθήκη</span>
        </div>
      </div>
      <div class="card-header-icon">
        <RouterLink
          :to="{ name: 'content.home.promo-cards.list' }"
          class="button"
        >
          <span class="icon is-small"><i class="fa fa-list"/></span>
          <span>Λίστα</span>
        </RouterLink>
      </div>
    </div>

    <form @submit.prevent="submit">
      <div class="card-content">
        <AddPhotos
          :title="'Εικόνα/Animation (WEBP)'"
          :preview="preview"
          :show-error="errors.has('image')"
          rule="image/webp"
          @addPhotos="handleAddPhotos"
        />

        <div class="columns is-multiline">
          <!-- Titles -->
          <div class="column is-half">
            <div class="field">
              <label class="label">Τίτλος (EL) *</label>
              <div class="control">
                <input
                  v-validate="'required|max:255'"
                  v-model.trim="form.title_el"
                  class="input"
                  type="text"
                  name="title_el"
                />
                <p v-show="errors.has('title_el')" class="help is-danger">
                  Εισάγετε τίτλο στα Ελληνικά (έως 255)
                </p>
              </div>
            </div>
          </div>

          <div class="column is-half">
            <div class="field">
              <label class="label">Τίτλος (EN) *</label>
              <div class="control">
                <input
                  v-validate="'required|max:255'"
                  v-model.trim="form.title_en"
                  class="input"
                  type="text"
                  name="title_en"
                />
                <p v-show="errors.has('title_en')" class="help is-danger">
                  Εισάγετε τίτλο στα Αγγλικά (έως 255)
                </p>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="column is-half">
            <div class="field">
              <label class="label">Περιεχόμενο (EL) *</label>
              <div class="control">
                <textarea
                  v-validate="'required'"
                  v-model.trim="form.content_el"
                  class="textarea"
                  name="content_el"
                  rows="4"
                />
                <p v-show="errors.has('content_el')" class="help is-danger">
                  Εισάγετε περιεχόμενο στα Ελληνικά
                </p>
              </div>
            </div>
          </div>

          <div class="column is-half">
            <div class="field">
              <label class="label">Περιεχόμενο (EN) *</label>
              <div class="control">
                <textarea
                  v-validate="'required'"
                  v-model.trim="form.content_en"
                  class="textarea"
                  name="content_en"
                  rows="4"
                />
                <p v-show="errors.has('content_en')" class="help is-danger">
                  Εισάγετε περιεχόμενο στα Αγγλικά
                </p>
              </div>
            </div>
          </div>

          <!-- Link + link texts -->
          <div class="column is-full">
            <div class="field">
              <label class="label">Link (URL) *</label>
              <div class="control">
                <input
                  v-validate="'required|url|max:2048'"
                  v-model.trim="form.link"
                  class="input"
                  type="text"
                  name="link"
                  placeholder="https://..."
                />
                <p v-show="errors.has('link')" class="help is-danger">
                  Εισάγετε έγκυρο link (έως 2048)
                </p>
              </div>
            </div>
          </div>

          <div class="column is-half">
            <div class="field">
              <label class="label">Κείμενο Συνδέσμου (EL)</label>
              <div class="control">
                <input
                  v-validate="'max:255'"
                  v-model.trim="form.link_text_el"
                  class="input"
                  type="text"
                  name="link_text_el"
                />
                <p v-show="errors.has('link_text_el')" class="help is-danger">
                  Έως 255 χαρακτήρες
                </p>
              </div>
            </div>
          </div>

          <div class="column is-half">
            <div class="field">
              <label class="label">Κείμενο Συνδέσμου (EN)</label>
              <div class="control">
                <input
                  v-validate="'max:255'"
                  v-model.trim="form.link_text_en"
                  class="input"
                  type="text"
                  name="link_text_en"
                />
                <p v-show="errors.has('link_text_en')" class="help is-danger">
                  Έως 255 χαρακτήρες
                </p>
              </div>
            </div>
          </div>

          <!-- Active -->
          <div class="column is-full">
            <div class="field">
              <label class="label" title="Έως 2 ενεργές κάρτες">
                Εμφανές *
              </label>
              <div class="control">
                <ToggleButton
                  :sync="true"
                  :value="form.is_active"
                  :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                  :width="30"
                  :height="15"
                  @change="updateActive"
                />
              </div>
              <p class="help">Έως 2 ενεργές κάρτες.</p>
            </div>
          </div>

          <!-- Order -->
          <div class="column is-full">
            <div class="field">
              <label class="label">Σειρά *</label>
              <div class="control">
                <input
                  v-validate="'required|numeric|min_value:0'"
                  v-model.number="form.order"
                  class="input"
                  type="number"
                  name="order"
                  min="0"
                />
                <p v-show="errors.has('order')" class="help is-danger">
                  Η σειρά πρέπει να είναι ≥ 0
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card-footer">
        <FormSubmit :is-saving="isSaving" @reset="reset" />
      </div>
    </form>
  </div>
</template>

<script>
import request from '@/utils/request';
import FormSubmit from '@/views/components/FormSubmit';
import AddPhotos from '@/views/components/AddPhotos';

export default {
  name: 'PromoCardsAdd',
  components: { FormSubmit, AddPhotos },

  data() {
    return {
      form: {
        title_el: '',
        title_en: '',
        content_el: '',
        content_en: '',
        link: '',
        link_text_el: '',
        link_text_en: '',
        is_active: false,
        order: 0,
        image: null,
      },
      preview: '',
      isSaving: false,
    };
  },

  methods: {
    updateActive({ value }) {
      this.form.is_active = !!value;
    },

    handleAddPhotos(image) {
      this.form.image = image;

      const reader = new FileReader();
      this.$validator
        .validateAll()
        .then(() => {
          reader.onload = e => {
            this.preview = e.target.result;
            this.errors.remove('image');
          };
          reader.readAsDataURL(image);
        })
        .catch(err => console.log(err));
    },

    async submit() {
      try {
        const ok = await this.$validator.validateAll();

        if (!this.preview) {
          this.errors.add({ field: 'image', msg: 'Απαιτείται εικόνα' });
        }
        if (!ok || !this.preview || !this.form.image) {
          if (!this.form.image) {
            this.errors.add({ field: 'image', msg: 'Απαιτείται εικόνα' });
          }
          throw new Error('Client-side validation failed');
        }

        this.isSaving = true;

        const fd = new FormData();
        fd.append('title_el', this.form.title_el);
        fd.append('title_en', this.form.title_en);
        fd.append('content_el', this.form.content_el);
        fd.append('content_en', this.form.content_en);
        fd.append('link', this.form.link);
        if (this.form.link_text_el)
          fd.append('link_text_el', this.form.link_text_el);
        if (this.form.link_text_en)
          fd.append('link_text_en', this.form.link_text_en);
        fd.append('is_active', this.form.is_active ? '1' : '0');
        fd.append('order', String(this.form.order));
        fd.append('image', this.form.image);

        const { data, status } = await request.post('/home/promo-cards', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        this.isSaving = false;

        if (status !== 201) {
          throw new Error(data?.message || 'To αίτημα απέτυχε');
        }

        this.$router
          .push({ name: 'content.home.promo-cards.list' })
          .catch(() => {});
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η κάρτα αποθηκεύτηκε',
        });
      } catch (err) {
        this.isSaving = false;
        const msg =
          err?.response?.data?.message || err?.message || 'To αίτημα απέτυχε';
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: msg.includes('promo cards can be active')
            ? 'Επιτρέπονται έως 2 ενεργές κάρτες. Απενεργοποίησε πρώτα μία από τις ενεργές.'
            : msg,
        });
      }
    },

    reset() {
      this.form = {
        title_el: '',
        title_en: '',
        content_el: '',
        content_en: '',
        link: '',
        link_text_el: '',
        link_text_en: '',
        is_active: false,
        order: 0,
        image: null,
      };
      this.preview = '';
      this.$validator.reset();
      this.errors.remove('image');
    },
  },
};
</script>
